import { message } from 'antd';
import { _oidcAuthorizeUrl, type FileItem } from '../services/aiAssistant';

/** 待恢复的对话上下文（授权跳转前保存，授权回来后恢复） */
export interface PendingChat {
  message: string;
  files?: FileItem[];
  projectId?: string;
  title?: string;
  /** 授权完成后应该跳回的前端路径（含 base） */
  returnPath: string;
  /** 标记，避免授权回来后又一次 4001 死循环 */
  oidcRetryCount?: number;
  /** 来源：胶囊浮窗 AI（true）/ aiAssistant 全屏 chat 页（undefined） */
  floating?: boolean;
}

const PENDING_KEY = 'ai-assistant:oidc-pending-chat';
const MAX_OIDC_RETRY = 1;

/** 判断一个错误是否为后端要求 OIDC 授权 */
export function isOidcRequiredError(err: any): boolean {
  return !!(err && (err.isOidcRequired || err.statusCode === 4001));
}

/** 保存待恢复对话 */
export function savePendingChat(pending: PendingChat) {
  try {
    sessionStorage.setItem(PENDING_KEY, JSON.stringify(pending));
  } catch {
    // 存储不可用时忽略
  }
}

/** 读取待恢复对话 */
export function readPendingChat(): PendingChat | null {
  try {
    const raw = sessionStorage.getItem(PENDING_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as PendingChat;
  } catch {
    return null;
  }
}

/** 清除待恢复对话 */
export function clearPendingChat() {
  try {
    sessionStorage.removeItem(PENDING_KEY);
  } catch {
    // 忽略
  }
}

/**
 * 触发 OIDC 授权跳转。
 *
 * 1. 保存 pending chat 到 sessionStorage；
 * 2. 调后端拿 authorizeUrl；
 * 3. 跳转浏览器到网易登录页。
 */
export async function redirectToOidcAuthorize(pending: PendingChat): Promise<void> {
  // 防止授权回来又遇到 4001 时无限循环
  const retryCount = pending.oidcRetryCount ?? 0;
  if (retryCount >= MAX_OIDC_RETRY) {
    clearPendingChat();
    message.error('授权失败，请稍后重试或联系管理员');
    return;
  }

  savePendingChat({ ...pending, oidcRetryCount: retryCount + 1 });

  try {
    const res = await _oidcAuthorizeUrl();
    const authorizeUrl = res?.authorizeUrl;
    if (!authorizeUrl || !/^https?:\/\//i.test(authorizeUrl)) {
      clearPendingChat();
      message.error('授权地址异常，请稍后重试');
      return;
    }
    window.location.href = authorizeUrl;
  } catch {
    clearPendingChat();
    message.error('获取授权地址失败，请稍后重试');
  }
}
